import React, { memo } from 'react';
import { usePremium } from '../contexts/PremiumContext';

/**
 * ⭐ PREMIUM STATUS BADGE - Innere Komponente für Premium-Info
 * Diese Komponente MUSS innerhalb des PremiumProvider sein!
 */
export const PremiumStatusBadge = memo(function PremiumStatusBadge({ t, colors, setShowPaywall }) {
  const { isPremium, isTrial, trialDaysLeft, trialExpired } = usePremium();
  
  // Trial aktiv
  if (isTrial) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
        border: '1px solid #3b82f6',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
        width: '100%',
        textAlign: 'center'
      }}>
        <span style={{ fontSize: '16px', color: '#1e40af' }}>
          🎁 {t('premium.trialActive')} - {t('premium.trialDaysLeft').replace('{{days}}', trialDaysLeft)}
        </span>
      </div>
    );
  }
  
  // Trial abgelaufen
  if (trialExpired && !isPremium) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
        border: '1px solid #f59e0b',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
        width: '100%',
        textAlign: 'center'
      }}>
        <span style={{ fontSize: '14px', color: '#92400e' }}>
          ⚠️ {t('premium.trialExpired')} - {t('premium.trialExpiredNote')}
        </span>
        <button
          onClick={() => setShowPaywall(true)}
          style={{ 
            marginTop: '8px',
            padding: '8px 16px', 
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', 
            color: '#1a1a1a', 
            border: 'none', 
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
            display: 'block',
            width: '100%'
          }}
        >
          ⭐ {t('premium.unlockNow')}
        </button>
      </div>
    );
  }
  
  // Premium aktiv oder kein Status
  return null;
});

/**
 * 🎁 TRIAL BANNER - Prominenter Banner oben in der App
 * Zeigt Trial-Status, Ablauf-Warnung oder Premium-Badge an
 */
export const TrialBanner = memo(function TrialBanner({ t, colors, setShowPaywall, isAuthenticated }) {
  const { isPremium, isTrial, trialDaysLeft, trialExpired, premiumPlan, premiumUntil, isLoading } = usePremium();

  // Nicht eingeloggt oder Loading → nichts anzeigen
  if (!isAuthenticated || isLoading) return null;

  // 🔄 Kulanz nach Abo-Ablauf: Zugriff läuft noch, aber Hinweis + „Erneuern"
  if (isPremium && premiumPlan === 'grace') {
    const bis = premiumUntil ? new Date(premiumUntil).toLocaleDateString('de-DE') : '';
    return (
      <div style={{
        background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '1px solid #f59e0b',
        borderRadius: '10px', padding: '10px 14px', marginBottom: '10px', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', gap: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#92400e', marginBottom: '2px' }}>
            ⚠️ Dein Abo ist abgelaufen
          </div>
          <div style={{ fontSize: '12px', color: '#a16207' }}>
            Du hast noch bis <b>{bis}</b> Zugriff (Kulanz). Danach wird Premium gesperrt.
          </div>
        </div>
        <button onClick={() => setShowPaywall(true)}
          style={{ padding: '8px 14px', background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: '#1a1a1a',
            border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Jetzt erneuern
        </button>
      </div>
    );
  }

  // Bezahltes Premium → kleines grünes Badge
  if (isPremium && !isTrial) return null;
  
  // Trial aktiv
  if (isTrial && isPremium) {
    const isLastDay = trialDaysLeft <= 1;
    const isWarning = trialDaysLeft <= 3;
    
    return (
      <div style={{
        background: isWarning 
          ? 'linear-gradient(135deg, #fef3c7, #fde68a)' 
          : 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
        border: `1px solid ${isWarning ? '#f59e0b' : '#3b82f6'}`,
        borderRadius: '10px',
        padding: '10px 14px',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ 
            fontSize: '13px', 
            fontWeight: 600, 
            color: isWarning ? '#92400e' : '#1e40af',
            marginBottom: '2px',
          }}>
            {isWarning ? '⚠️' : '🎁'} {t('premium.trialActive')}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: isWarning ? '#a16207' : '#2563eb',
          }}>
            {isLastDay 
              ? (t('premium.trialLastDay') || '⏰ Letzter Tag! Morgen endet die Testphase.')
              : t('premium.trialDaysLeft').replace('{{days}}', trialDaysLeft)
            }
          </div>
          <div style={{
            fontSize: '11px',
            color: isWarning ? '#a16207' : '#3b82f6',
            marginTop: '2px',
            opacity: 0.8,
          }}>
            ☁️ Cloud-Sync & Multi-Device aktiv
          </div>
        </div>
        {isWarning && (
          <button
            onClick={() => setShowPaywall(true)}
            style={{
              padding: '6px 12px',
              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              color: '#1a1a1a',
              border: 'none',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            ⭐ Jetzt upgraden
          </button>
        )}
      </div>
    );
  }
  
  // Trial abgelaufen & kein Premium
  if (trialExpired && !isPremium) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
        border: '1px solid #ef4444',
        borderRadius: '10px',
        padding: '10px 14px',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#991b1b', marginBottom: '2px' }}>
            ❌ {t('premium.trialExpired') || 'Testphase abgelaufen'}
          </div>
          <div style={{ fontSize: '12px', color: '#b91c1c' }}>
            {t('premium.trialExpiredNote') || 'Cloud-Sync deaktiviert. Daten werden nur lokal gespeichert.'}
          </div>
        </div>
        <button
          onClick={() => setShowPaywall(true)}
          style={{
            padding: '6px 12px',
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          ⭐ {t('premium.unlockNow') || 'Freischalten'}
        </button>
      </div>
    );
  }
  
  // Eingeloggt aber ohne Trial/Premium (sollte nicht vorkommen, Trial startet automatisch)
  if (!isPremium && !trialExpired) {
    return (
      <div style={{
        background: colors.darkMode ? '#1e293b' : '#f1f5f9',
        border: `1px solid ${colors.darkMode ? '#334155' : '#cbd5e1'}`,
        borderRadius: '10px',
        padding: '10px 14px',
        marginBottom: '10px',
      }}>
        <div style={{ fontSize: '12px', color: colors.darkMode ? '#94a3b8' : '#64748b' }}>
          ☁️ Cloud-Sync ist nur mit Premium verfügbar
        </div>
      </div>
    );
  }
  
  return null;
});

